import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, InputGroup, FormControl } from 'react-bootstrap';
import {
  FaUser, FaGraduationCap, FaEnvelope, FaPhone, FaCalendar,
  FaIdCard, FaHome, FaBook, FaUserTie, FaHeart, FaSearch, 
  FaBuilding, FaLock, FaGlobe, FaVenusMars, FaAddressCard, 
  FaBriefcase, FaMoneyBillWave, FaUsers, FaUniversity, FaNetworkWired
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';
import axios from '../api/axios';
import GenericCrudPage from '../components/GenericCrudPage';
import GenericViewModal from '../components/GenericViewModal';
import {
  useColleges,
  useDegrees,
  useDepartments,
  useDisciplines,
  useDesignations,
  useStaffTypes,
} from '../hooks/useDropdownData';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// Blood group options
const BLOOD_GROUP_OPTIONS = [
  { value: 'A+', label: 'A+' },
  { value: 'A-', label: 'A-' },
  { value: 'B+', label: 'B+' },
  { value: 'B-', label: 'B-' },
  { value: 'AB+', label: 'AB+' },
  { value: 'AB-', label: 'AB-' },
  { value: 'O+', label: 'O+' },
  { value: 'O-', label: 'O-' }
];

// Gender options
const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' }
];

// Common state options (you can expand this)
const STATE_OPTIONS = [
  { value: 'Karnataka', label: 'Karnataka' },
  { value: 'Tamil Nadu', label: 'Tamil Nadu' },
  { value: 'Andhra Pradesh', label: 'Andhra Pradesh' },
  { value: 'Telangana', label: 'Telangana' },
  { value: 'Kerala', label: 'Kerala' },
  { value: 'Maharashtra', label: 'Maharashtra' },
  { value: 'Gujarat', label: 'Gujarat' },
  { value: 'Rajasthan', label: 'Rajasthan' },
  { value: 'Uttar Pradesh', label: 'Uttar Pradesh' },
  { value: 'West Bengal', label: 'West Bengal' },
  { value: 'Bihar', label: 'Bihar' },
  { value: 'Odisha', label: 'Odisha' },
  { value: 'Madhya Pradesh', label: 'Madhya Pradesh' },
  { value: 'Chhattisgarh', label: 'Chhattisgarh' },
  { value: 'Jharkhand', label: 'Jharkhand' },
  { value: 'Assam', label: 'Assam' },
  { value: 'Punjab', label: 'Punjab' },
  { value: 'Haryana', label: 'Haryana' },
  { value: 'Himachal Pradesh', label: 'Himachal Pradesh' },
  { value: 'Uttarakhand', label: 'Uttarakhand' },
  { value: 'Goa', label: 'Goa' },
  { value: 'Tripura', label: 'Tripura' },
  { value: 'Meghalaya', label: 'Meghalaya' },
  { value: 'Manipur', label: 'Manipur' },
  { value: 'Nagaland', label: 'Nagaland' },
  { value: 'Arunachal Pradesh', label: 'Arunachal Pradesh' },
  { value: 'Mizoram', label: 'Mizoram' },
  { value: 'Sikkim', label: 'Sikkim' }
];

// Utility functions for date conversion
const formatDateForAPI = (dateString) => {
  if (!dateString) return "";
  // If it's already in DD/MM/YYYY format, return as is
  if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) return dateString;
  // If it's in YYYY-MM-DD format (from date input), convert to DD/MM/YYYY
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateString.split("-");
    return `${parseInt(day)}/${parseInt(month)}/${year}`;
  }
  return dateString;
};

const convertToInputDateFormat = (dateStr) => {
  if (!dateStr) return "";
  // If already in YYYY-MM-DD format, return as is
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) return dateStr;
  // If in DD/MM/YYYY format, convert to YYYY-MM-DD
  if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
    const parts = dateStr.split("/");
    if (parts.length === 3) {
      const [day, month, year] = parts;
      if (day && month && year && year.length === 4) {
        return `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      }
    }
  }
  return "";
};

// Utility function to encode password (Base64)
const encodePassword = (password) => {
  if (!password) return "";
  return btoa(password); // Base64 encode
};

// Utility function to decode password (Base64)
const decodePassword = (encodedPassword) => {
  if (!encodedPassword) return "";
  try {
    return atob(encodedPassword); // Base64 decode
  } catch (error) {
    console.warn("Failed to decode password:", error);
    return "";
  }
};

const StaffProfile = () => {
  const [filterDegree, setFilterDegree] = useState("");
  const [searchStaffId, setSearchStaffId] = useState("");
  const [fetchConfig, setFetchConfig] = useState({ degree: "" });
  const [showStaffViewModal, setShowStaffViewModal] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);

  // Load dropdown data
  const { colleges, loading: collegesLoading } = useColleges();
  const { degrees, loading: degreesLoading } = useDegrees();
  const { departments, loading: departmentsLoading } = useDepartments();
  const { disciplines, loading: disciplinesLoading } = useDisciplines();  
  const { designations, loading: designationsLoading } = useDesignations();
  const { staffTypes, loading: staffTypesLoading } = useStaffTypes();

  const dropdownsLoading = collegesLoading || degreesLoading || departmentsLoading || 
                           disciplinesLoading || designationsLoading || staffTypesLoading;

  // Custom fetch function for staff data
  const customFetch = async () => {
    const token = Cookies.get('token');
    const getAllUrl = `${BASE_URL}/academic/getAllStaff`;
    const getByIdUrl = `${BASE_URL}/academic/getStaffById`;

    // This helper function is a workaround for the main `getAllStaff` endpoint
    // not returning the `exIsActive` field. It fetches the full details for each
    // staff member to ensure the status is available for the table.
    // This can be slow if there are many staff members. The ideal solution is
    // for the backend API to include `exIsActive` in the `getAllStaff` response.
    const fetchFullStaffDetails = async (staffList) => {
      if (!staffList || staffList.length === 0) {
        return [];
      }
      try {
        const fullProfiles = await Promise.all(
          staffList.map(staff =>
            axios.get(`${getByIdUrl}?id=${staff.id}`, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(res => {
              const profileData = res.data?.Staff;
              return Array.isArray(profileData) ? profileData[0] : profileData;
            }).catch(err => {
              console.error(`Failed to fetch full profile for staff ID ${staff.id}`, err);
              return staff; // On error, return the original (potentially stale) data
            })
          )
        );
        return fullProfiles.filter(Boolean); // Filter out any null/undefined results
      } catch (error) {
        console.error("Error during bulk staff profile fetch:", error);
        return staffList; // Return original list on catastrophic failure
      }
    };

    // Case 1: Search by specific staff ID
    if (searchStaffId) {
      try {
        const res = await axios.get(`${getByIdUrl}?id=${searchStaffId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const staffData = res.data?.Staff;
        return staffData ? (Array.isArray(staffData) ? staffData : [staffData]) : [];
      } catch (error) {
        console.error("Failed to fetch staff by ID:", error);
        toast.error("Could not fetch staff data for the provided ID.");
        return [];
      }
    }
    // Case 2: Filter by degree
    else if (fetchConfig.degree) {
      try {
        const res = await axios.get(`${getAllUrl}?degree=${fetchConfig.degree}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const initialList = res.data?.Staff || [];
        return await fetchFullStaffDetails(initialList);
      } catch (error) {
        console.error("Failed to fetch staff by degree:", error);
        toast.error("Could not fetch staff data for the selected degree.");
        return [];
      }
    }
    // Case 3: Fetch all staff for all degrees
    else {
      try {
        // Optimized to make a single call to get all staff
        const res = await axios.get(getAllUrl, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const initialList = res.data?.Staff || [];
        return await fetchFullStaffDetails(initialList);
      } catch (error) {
        console.error("Failed to fetch all staff:", error);
        toast.error("Could not fetch staff data.");
        return [];
      }
    }
  };

  // Fetch single staff for view/edit
  const fetchStaffById = async (item) => {
    if (!item || !item.id) {
      toast.error("Invalid staff selected.");
      return null;
    }
    try {
      const token = Cookies.get('token');
      const res = await axios.get(`${BASE_URL}/academic/getStaffById?id=${item.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const staffData = res.data?.Staff?.[0];
      return staffData || null;
    } catch (error) {
      console.error(`Failed to fetch staff by ID ${item.id}:`, error);
      toast.error("An error occurred while fetching staff details.");
      return null;
    }
  };

  // Enhanced form validation
  const validateForm = (form) => {
    const errors = {};
    
    // Required field validation
    if (!form.firstName?.trim()) errors.firstName = 'First Name is required';
    if (!form.email?.trim()) errors.email = 'Email is required';
    if (!form.mobile?.trim()) errors.mobile = 'Mobile is required';
    if (!form.employeeId?.trim()) errors.employeeId = 'Employee ID is required';
    if (!form.colleges || form.colleges.length === 0) errors.colleges = 'College is required';
    if (!form.department) errors.department = 'Department is required';
    if (!form.designation) errors.designation = 'Designation is required';
    if (!form.stafftype) errors.stafftype = 'Staff Type is required';
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (form.email && !emailRegex.test(form.email)) {
      errors.email = "Please enter a valid email address";
    }

    // Mobile validation (10 digits)
    if (form.mobile && !/^\d{10}$/.test(form.mobile.replace(/[^\d]/g, ''))) {
      errors.mobile = "Please enter a valid 10-digit mobile number";
    }

    // Employee ID validation (alphanumeric)
    if (form.employeeId && !/^[A-Za-z0-9]+$/.test(form.employeeId)) {
      errors.employeeId = "Employee ID should contain only letters and numbers";
    }

    // Aadhaar number validation (12 digits with optional hyphens)
    if (form.adhaarNumber) {
      const aadhaarDigits = form.adhaarNumber.replace(/[^\d]/g, '');
      if (aadhaarDigits.length !== 12) {
        errors.adhaarNumber = "Aadhaar number should be 12 digits";
      }
    }

    // PAN number validation (5 letters, 4 digits, 1 letter)
    if (form.panNumber && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(form.panNumber.toUpperCase())) {
      errors.panNumber = "Please enter a valid PAN number (e.g., ABCDE1234F)";
    }

    // Pincode validation (6 digits)
    if (form.pincode && !/^\d{6}$/.test(form.pincode)) {
      errors.pincode = "Pincode should be 6 digits";
    }

    // IFSC code validation (4 letters, 7 alphanumeric)
    if (form.ifscCode && !/^[A-Z]{4}[A-Z0-9]{7}$/.test(form.ifscCode.toUpperCase())) {
      errors.ifscCode = "Please enter a valid IFSC code (e.g., SBIN0001234)";
    }

    // Date validation
    if (form.dateOfBirth && form.dateOfJoining) {
      const dob = new Date(form.dateOfBirth);
      const doj = new Date(form.dateOfJoining);
      if (doj < dob) {
        errors.dateOfJoining = "Date of joining cannot be before date of birth";
      }
    }

    return errors;
  };

  // Configuration for CRUD operations
  const config = {
    baseUrl: BASE_URL,
    endpoints: {
      fetch: '/academic/getAllStaff', // Base endpoint, but customFetch will be used
      save: '/academic/createStaff',
      update: '/academic/updateStaff',
      delete: '/academic/deleteStaff'
    },
    validateForm: validateForm,
    initialFormState: {
      employeeId: "",
      colleges: [],
      degree: "",
      department: "",
      discipline: "",
      designation: "",
      stafftype: "",
      loginName: "",
      password: "",
      email: "",
      firstName: "",
      middleName: "",
      lastName: "",
      gender: "",
      bloodGroup: "",
      adhaarNumber: "",
      mobile: "",
      dateOfBirth: "",
      dateOfJoining: "",
      panNumber: "",
      bankAccountNo: "",
      bankName: "",
      ifscCode: "",
      address: "",
      state: "",
      pincode: "",
      city: "",
      skill: "",
      ipAddress: "",
      macAddress: "",
      exIsActive: 1,
      user: 1,
    },
    entityName: 'Staff',
    fetchMethod: 'CUSTOM',
    deleteMethod: 'GET',
    customFetch: customFetch,
  };

  // Transform form data for API submission (updated to match your payload structure)
  const transformPayload = (form, editId, userId) => {
    const payload = {
      // Basic Information
      employeeId: form.employeeId?.toString() || "",
      colleges: Array.isArray(form.colleges) ? form.colleges.map(id => Number(id)).filter(Boolean) : (form.colleges ? [Number(form.colleges)] : []),
      department: Number(form.department),
      discipline: Number(form.discipline) || undefined,
      designation: Number(form.designation),
      stafftype: Number(form.stafftype),
      
      // Authentication
      loginName: form.loginName?.toString() || "",
      password: form.password ? encodePassword(form.password) : "",
      
      // Personal Information
      firstName: form.firstName?.toString() || "",
      middleName: form.middleName?.toString() || "",
      lastName: form.lastName?.toString() || "",
      gender: form.gender?.toString() || "",
      bloodGroup: form.bloodGroup?.toString() || "",
      dateOfBirth: formatDateForAPI(form.dateOfBirth),
      dateOfJoining: formatDateForAPI(form.dateOfJoining),
      adhaarNumber: form.adhaarNumber?.toString() || "",
      panNumber: form.panNumber?.toString() || "",
      
      // Contact Information
      email: form.email?.toString() || "",
      mobile: form.mobile?.toString() || "",
      address: form.address?.toString() || "",
      city: form.city?.toString() || "",
      state: form.state?.toString() || "",
      pincode: form.pincode?.toString() || "",
      
      // Banking Details
      bankName: form.bankName?.toString() || "",
      bankAccountNo: form.bankAccountNo?.toString() || "",
      ifscCode: form.ifscCode?.toString() || "",
      
      // Professional Information
      skill: form.skill?.toString() || "",
      
      // System Information
      ipAddress: form.ipAddress?.toString() || "",
      macAddress: form.macAddress?.toString() || "",
      exIsActive: Number(form.exIsActive),
      user: Number(userId) || 1,
    };

    // For update, add id and remove degree field
    if (editId) {
      payload.id = editId;
    } else {
      // For create, add degree if provided
      if (form.degree) {
        payload.degree = Number(form.degree);
      }
    }

    // Remove undefined values
    Object.keys(payload).forEach(key => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    return payload;
  };

  // Transform API data to form state for editing
  const transformToForm = (item) => {
    if (!item) return config.initialFormState;

    // Handle colleges - ensure it's an array of strings for the form's multi-select
    let collegeIds = [];
    if (Array.isArray(item.exCollegeId)) {
      collegeIds = item.exCollegeId.map(id => id.toString());
    } else if (item.exCollegeId) {
      collegeIds = [item.exCollegeId.toString()];
    }

    return {
      ...config.initialFormState,
      ...item,
      colleges: collegeIds,
      department: item.departmentId?.toString() || "",
      designation: item.designationId?.toString() || "",
      stafftype: item.stafftypeId?.toString() || "",
      discipline: item.disciplineId?.toString() || "",
      dateOfBirth: convertToInputDateFormat(item.dateOfBirth),
      dateOfJoining: convertToInputDateFormat(item.dateOfJoining),
      email: item.exEmail || item.email || "",
      password: item.password ? decodePassword(item.password) : "",
      exIsActive: item.exIsActive === 1 || item.exIsActive === true,
    };
  };

  // Enhanced form fields organized into sections
  const formFields = [
    {
      title: '🏢 Basic Information',
      fields: [
        { name: 'employeeId', label: 'Employee ID', icon: FaIdCard, required: true, placeholder: 'e.g., B89X2X32501234' },
      // NEW - This will create the dropdown multiselect
{ 
  name: 'colleges', 
  label: 'Colleges', 
  icon: FaBuilding, 
  required: true, 
  as: 'multiselect',  // Changed from 'select' to 'multiselect'
  options: (Array.isArray(colleges) ? colleges : []).filter(Boolean).map(c => ({ 
    value: c.id.toString(), 
    label: c.exName 
  })),
  loading: collegesLoading,
  placeholder: 'Select colleges...'
},
        { name: 'degree', label: 'Degree', icon: FaGraduationCap, as: 'select', 
          options: (Array.isArray(degrees) ? degrees : []).filter(Boolean).map(d => ({ value: d.id, label: d.exName })),
          hideOnEdit: true },
        { name: 'department', label: 'Department', icon: FaBuilding, required: true, as: 'select',
          options: (Array.isArray(departments) ? departments : []).filter(Boolean).map(d => ({ value: d.id, label: d.exDepartmentName })) },
        { name: 'designation', label: 'Designation', icon: FaUserTie, required: true, as: 'select',
          options: (Array.isArray(designations) ? designations : []).filter(Boolean).map(d => ({ value: d.id, label: d.exName })) },
        { name: 'stafftype', label: 'Staff Type', icon: FaUsers, required: true, as: 'select',
          options: (Array.isArray(staffTypes) ? staffTypes : []).filter(Boolean).map(s => ({ value: s.id, label: s.exName })) },
        { name: 'discipline', label: 'Discipline', icon: FaBook, as: 'select',
          options: (Array.isArray(disciplines) ? disciplines : []).filter(Boolean).map(d => ({ value: d.id, label: d.exName })) },
      ]
    },
    {
      title: '👤 Personal Details',
      fields: [
        { name: 'firstName', label: 'First Name', icon: FaUser, required: true, placeholder: 'Enter first name' },
        { name: 'middleName', label: 'Middle Name', icon: FaUser, placeholder: 'Enter middle name' },
        { name: 'lastName', label: 'Last Name', icon: FaUser, placeholder: 'Enter last name' },
        { name: 'gender', label: 'Gender', icon: FaVenusMars, as: 'select', 
          options: GENDER_OPTIONS },
        { name: 'bloodGroup', label: 'Blood Group', icon: FaHeart, as: 'select',
          options: BLOOD_GROUP_OPTIONS },
        { name: 'dateOfBirth', label: 'Date of Birth', icon: FaCalendar, type: 'date' },
        { name: 'dateOfJoining', label: 'Date of Joining', icon: FaCalendar, type: 'date' },
        { name: 'adhaarNumber', label: 'Aadhaar Number', icon: FaAddressCard, placeholder: 'e.g., 909-090-090-000' },
        { name: 'panNumber', label: 'PAN Number', icon: FaAddressCard, placeholder: 'e.g., ABCDE1234F' },
      ]
    },
    {
      title: '📞 Contact Information',
      fields: [
        { name: 'email', label: 'Email', icon: FaEnvelope, required: true, type: 'email', placeholder: 'user@example.com' },
        { name: 'mobile', label: 'Mobile', icon: FaPhone, required: true, placeholder: '10-digit mobile number' },
        { name: 'address', label: 'Address', icon: FaHome, as: 'textarea', rows: 3, placeholder: 'Enter complete address' },
        { name: 'city', label: 'City', icon: FaHome, placeholder: 'Enter city name' },
        { name: 'state', label: 'State', icon: FaHome, as: 'select',
          options: STATE_OPTIONS },
        { name: 'pincode', label: 'Pincode', icon: FaHome, placeholder: '6-digit pincode' },
      ]
    },
    {
      title: '🏦 Banking Details',
      fields: [
        { name: 'bankName', label: 'Bank Name', icon: FaMoneyBillWave, placeholder: 'e.g., State Bank of India' },
        { name: 'bankAccountNo', label: 'Account Number', icon: FaMoneyBillWave, placeholder: 'Bank account number' },
        { name: 'ifscCode', label: 'IFSC Code', icon: FaMoneyBillWave, placeholder: 'e.g., SBIN0001234' },
      ]
    },
    {
      title: '⚙️ System Information',
      fields: [
        { name: 'loginName', label: 'Login Name', icon: FaUser, placeholder: 'Username for system login' },
        { name: 'password', label: 'Password', icon: FaLock, type: 'password', placeholder: 'Enter password' },
        { name: 'skill', label: 'Skills', icon: FaBriefcase, placeholder: 'e.g., Java, Python, Teaching' },
        { name: 'ipAddress', label: 'IP Address', icon: FaNetworkWired, placeholder: 'e.g., 192.168.1.1' },
        { name: 'macAddress', label: 'MAC Address', icon: FaNetworkWired, placeholder: 'e.g., 00:1B:44:11:3A:B7' },
        { name: 'exIsActive', label: 'Active Status', icon: FaUser, as: 'switch' },
      ]
    }
  ];

  // Enhanced table columns
  const tableColumns = [
    { key: 'employeeId', label: 'Employee ID', sortable: true },
    { 
      key: 'firstName', 
      label: 'Name', 
      sortable: true, 
      render: (item) => `${item.firstName || ''} ${item.middleName || ''} ${item.lastName || ''}`.trim() 
    },
    { key: 'exCollege', label: 'College(s)', sortable: true, render: (item) => item.exCollege || '-' },
    { key: 'exEmail', label: 'Email', sortable: true, render: (item) => item.exEmail || item.email },
    { key: 'mobile', label: 'Mobile', sortable: true },
    { key: 'exDegree', label: 'Degree', sortable: true },
    { key: 'designation', label: 'Designation', sortable: true },
  ];

  // Enhanced CSV headers
  const csvHeaders = [
    { key: 'employeeId', label: 'Employee ID' },
    { key: 'firstName', label: 'First Name' },
    { key: 'middleName', label: 'Middle Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'email', label: 'Email' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'gender', label: 'Gender' },
    { key: 'bloodGroup', label: 'Blood Group' },
    { key: 'dateOfBirth', label: 'Date of Birth' },
    { key: 'dateOfJoining', label: 'Date of Joining' },
    { key: 'exCollege', label: 'College(s)' },
    { key: 'exDegree', label: 'Degree' },
    { key: 'designation', label: 'Designation' },
    { key: 'department', label: 'Department' },
    { key: 'skill', label: 'Skills' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'exIsActive', label: 'Status', render: (item) => item.exIsActive ? 'Active' : 'Inactive' }
  ];

  // Enhanced view fields for modal
  const getViewFields = (item) => {
    if (!item) return [];
    return [
      // Basic Information
      { label: 'Employee ID', value: item.employeeId, icon: FaIdCard },
      { label: 'Full Name', value: `${item.firstName || ''} ${item.middleName || ''} ${item.lastName || ''}`.trim(), icon: FaUser },
      
      // Personal Information
      { label: 'Gender', value: item.gender, icon: FaVenusMars },
      { label: 'Blood Group', value: item.bloodGroup, icon: FaHeart },
      { label: 'Date of Birth', value: item.dateOfBirth, icon: FaCalendar },
      { label: 'Date of Joining', value: item.dateOfJoining, icon: FaCalendar },
      { label: 'Aadhaar Number', value: item.adhaarNumber, icon: FaAddressCard },
      { label: 'PAN Number', value: item.panNumber, icon: FaAddressCard },

      // Professional Information
      { label: 'College(s)', value: item.exCollege, icon: FaUniversity },
      { label: 'Degree', value: item.exDegree, icon: FaGraduationCap },
      { label: 'Department', value: item.department, icon: FaBuilding },
      { label: 'Designation', value: item.designation, icon: FaUserTie },
      { label: 'Staff Type', value: item.stafftype, icon: FaUsers },
      { label: 'Discipline', value: item.discipline, icon: FaBook },
      { label: 'Skills', value: item.skill, icon: FaBriefcase },

      // Contact Information
      { label: 'Email', value: item.exEmail || item.email, icon: FaEnvelope },
      { label: 'Mobile', value: item.mobile, icon: FaPhone },
      { label: 'Address', value: item.address, icon: FaHome, fullWidth: true },
      { label: 'City', value: item.city, icon: FaHome },
      { label: 'State', value: item.state, icon: FaHome },
      { label: 'Pincode', value: item.pincode, icon: FaHome },

      // Banking Details
      { label: 'Bank Name', value: item.bankName, icon: FaMoneyBillWave },
      { label: 'Account Number', value: item.bankAccountNo, icon: FaMoneyBillWave },
      { label: 'IFSC Code', value: item.ifscCode, icon: FaMoneyBillWave },

      // System Information
      { label: 'Login Name', value: item.loginName, icon: FaUser },
      { label: 'IP Address', value: item.ipAddress, icon: FaNetworkWired },
      { label: 'MAC Address', value: item.macAddress, icon: FaNetworkWired },
      { label: 'Status', value: item.exIsActive ? 'Active' : 'Inactive', icon: FaUser, 
        className: item.exIsActive ? 'text-success fw-bold' : 'text-danger fw-bold' },
    ];
  };

  // Handle search by ID
  const handleSearchById = async () => {
    if (!searchStaffId) {
      toast.error("Please enter a Staff ID to search.");
      return;
    }
    
    try {
      const token = Cookies.get('token');
      const res = await axios.get(`${BASE_URL}/academic/getStaffById?id=${searchStaffId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const staffData = res.data?.Staff?.[0];

      if (staffData) {
        setSelectedStaff(staffData);
        setShowStaffViewModal(true);
      } else {
        toast.warn("Staff not found with the provided ID.");
      }
    } catch (error) {
      console.error("Failed to fetch staff by ID:", error);
      toast.error("An error occurred while searching for the staff.");
    }
  };

  // Handle clear filters
  const handleClearFilters = () => {
    setFilterDegree('');
    setSearchStaffId('');
    setFetchConfig({ degree: "" });
  };

  // Handle export with enhanced data
  const handleExportData = (data) => {
    return data.map(item => ({
      ...item,
      fullName: `${item.firstName || ''} ${item.middleName || ''} ${item.lastName || ''}`.trim(),
      statusText: item.exIsActive ? 'Active' : 'Inactive',
      formattedMobile: item.mobile,
      formattedEmail: item.exEmail || item.email,
    }));
  };

  return (
    <Container fluid className="py-4">
      {dropdownsLoading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading dropdown data...</span>
          </div>
          <p className="mt-3 text-muted">Loading form data...</p>
        </div>
      ) : (
        <>
          {/* Enhanced Filter Section */}
          <Row className="mb-4 align-items-end">
            <Col md={4}>
              <Form.Label className="fw-semibold">
                <FaGraduationCap className="me-2" />
                Filter by Degree
              </Form.Label>
              <Form.Select
                value={filterDegree}
                onChange={(e) => {
                  const newDegree = e.target.value;
                  setFilterDegree(newDegree);
                  setSearchStaffId('');
                  setFetchConfig({ degree: newDegree });
                }}
                className="form-select-lg"
              >
                <option value="">🎓 All Degrees</option>
                {(Array.isArray(degrees) ? degrees : [])
                  .filter(Boolean)
                  .map((d) => (
                    <option key={d.id} value={d.id}>{d.exName}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={5}>
              <Form.Label className="fw-semibold">
                <FaSearch className="me-2" />
                Search by Staff ID
              </Form.Label>
              <InputGroup size="lg">
                <FormControl
                  placeholder="Enter Staff ID"
                  value={searchStaffId}
                  onChange={(e) => setSearchStaffId(e.target.value)}
                  onKeyPress={(e) => { if (e.key === 'Enter') handleSearchById(); }}
                />
                <Button 
                  variant="outline-primary" 
                  onClick={handleSearchById}
                  disabled={!searchStaffId.trim()}
                >
                  <FaSearch /> Search
                </Button>
              </InputGroup>
            </Col>
            <Col md={3} className="d-flex justify-content-start">
              <Button 
                variant="outline-secondary" 
                onClick={handleClearFilters}
                size="lg"
                className="d-flex align-items-center"
              >
                <span className="me-2">🔄</span>
                Clear Filters
              </Button>
            </Col>
          </Row>

          {/* Enhanced Statistics Row */}
          <Row className="mb-4">
            <Col>
              <div className="bg-light rounded p-3 border">
                <div className="d-flex align-items-center justify-content-between">
                  <div>
                    <h6 className="text-muted mb-1">Current Filter</h6>
                    <p className="mb-0 fw-semibold">
                      {filterDegree 
                        ? `Degree: ${degrees?.find(d => d.id == filterDegree)?.exName || 'Unknown'}` 
                        : searchStaffId 
                          ? `Staff ID: ${searchStaffId}`
                          : 'All Staff Members'
                      }
                    </p>
                  </div>
                  <div className="text-end">
                    <FaUsers className="text-primary" size={24} />
                  </div>
                </div>
              </div>
            </Col>
          </Row>

          <GenericCrudPage
            key={JSON.stringify(fetchConfig)}
            title="Staff"
            config={config}
            formFields={formFields}
            tableColumns={tableColumns}
            emptyIcon={FaUserTie}
            searchQuery={searchStaffId}
            setSearchQuery={setSearchStaffId}
            csvHeaders={csvHeaders}
            csvFilename="staff_members.csv"
            transformPayload={transformPayload}
            transformToForm={transformToForm}
            getViewFields={getViewFields}
            fetchItemForView={fetchStaffById}
            fetchItemForEdit={fetchStaffById}
            exportDataTransform={handleExportData}
          />

          <GenericViewModal
            show={showStaffViewModal}
            onHide={() => setShowStaffViewModal(false)}
            item={selectedStaff}
            title="Staff Details"
            fields={getViewFields(selectedStaff)}
          />
        </>
      )}
    </Container>
  );
};

export default StaffProfile;